import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { IconComponent } from '@libs/components/icons';
import { CepStateService } from '@libs/shared/common';
import { debounceTime, Subject } from 'rxjs';

@Component({
  selector: 'ui-search-overlay-header',
  templateUrl: './search-overlay-header.component.html',
  styleUrls: ['./search-overlay-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatButton,
    IconComponent,
  ],
})
export class SearchOverlayHeaderComponent implements AfterViewInit {
  public initialSearchText: InputSignal<string> = input('');
  public searchText: OutputEmitterRef<string> = output<string>();
  public enter: OutputEmitterRef<string> = output();
  public closed: OutputEmitterRef<void> = output();
  public searchFieldControl: FormControl<string> = new FormControl<string>('', { nonNullable: true });
  private readonly searchFieldElementRef: Signal<ElementRef<HTMLInputElement>> = viewChild.required('inputElement');
  private readonly searchField: Signal<HTMLInputElement> = computed(() => this.searchFieldElementRef().nativeElement);
  private readonly searchChanged$: Subject<KeyboardEvent> = new Subject();
  private readonly cepStateService: CepStateService = inject(CepStateService);

  public constructor() {
    this.searchChanged$.pipe(debounceTime(300), takeUntilDestroyed()).subscribe(() => {
      this.searchText.emit(this.searchFieldControl.value);
    });

    effect(() => {
      this.searchFieldControl.setValue(this.initialSearchText());
    });
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      this.searchField().focus();
    }, 200);
  }

  public onInputKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.enter.emit(this.searchFieldControl.value);

      return;
    }

    this.searchChanged$.next(event);
  }

  public clearInput(): void {
    this.searchFieldControl.setValue('');
    this.searchField().focus();
    this.searchText.emit('');
  }

  public onClose(): void {
    this.cepStateService.updateSearchParams({
      q: this.searchFieldControl.value,
      category: undefined,
    });
    this.closed.emit();
    this.searchField().blur();
  }
}
